<template>
  <div class="card">
    <div class="faq-nav">
      <div v-for="item in faqNav" :key="item.block" class="faq-nav__row">
        <a-collapse
            v-if="item.children"
            :activeKey="openGroup === item.block ? [item.block] : []"
            :bordered="false"
            class="faq-nav__collapse"
            @change="(keys) => onGroupToggle(item.block, keys)"
        >
          <template #expandIcon="{ isActive }">
            <UpOutlined v-if="isActive" />
            <DownOutlined v-else />
          </template>

          <a-collapse-panel
              :key="item.block"
              :header="item.label"
              class="faq-nav__panel"
              @click="goTo(item.block)"
          >
            <div
                v-for="child in item.children"
                :key="child.section"
                class="faq-nav__child"
                :class="{ 'is-active': isSectionActive(item.block, child.section) }"
                @click.prevent.stop="goTo(item.block, child.section)"
            >
              {{ child.label }}
            </div>
          </a-collapse-panel>
        </a-collapse>

        <div
            v-else
            class="faq-nav__item"
            :class="{ 'is-active': isBlockActive(item.block) }"
            @click.prevent="goTo(item.block)"
        >
          {{ item.label }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { DownOutlined, UpOutlined } from "@ant-design/icons-vue";

import { faqNav } from "@/mixins/faqNav";
import {
  getFaqRouteLocation,
  normalizeFaqParams,
} from "@/views/FAQ/faqRouteHelper";

export default {
  name: "FaqSideNavigation",
  components: {
    UpOutlined,
    DownOutlined,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const routeState = computed(() => normalizeFaqParams(route.params));
    const openGroup = ref(null);

    watch(
        routeState,
        (state) => {
          openGroup.value = state.block;
        },
        { immediate: true }
    );

    const goTo = (block, section = null) => {
      router.push(getFaqRouteLocation(block, section));
    };

    const onGroupToggle = (block, keys) => {
      openGroup.value = keys.length ? block : null;
    };

    const isBlockActive = (block) =>
        routeState.value.block === block && routeState.value.section === null;

    const isSectionActive = (block, section) =>
        routeState.value.block === block && routeState.value.section === section;

    return {
      faqNav,
      openGroup,
      goTo,
      onGroupToggle,
      isBlockActive,
      isSectionActive,
    };
  },
};
</script>

<style scoped>
.faq-nav {
  display: block;
}

.faq-nav__item {
  display: block;
  padding: 10px 12px;
  color: var(--main-font);
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
  border-radius: 8px;
  cursor: pointer;
}

.faq-nav__item.is-active {
  background: #f2f2f2;
  font-weight: 600;
}

.faq-nav__collapse :deep(.ant-collapse) {
  border: 0;
  background: transparent;
}

.faq-nav__collapse :deep(.ant-collapse-item) {
  border: 0;
  background: #ffffff;
  font-family: "Open Sans", sans-serif;
}

.faq-nav__collapse :deep(.ant-collapse-header) {
  padding: 10px 12px !important;
  background: transparent !important;
  color: var(--main-font) !important;
  font-size: 14px !important;
  line-height: 20px !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  padding-inline-start: 12px !important;
  font-family: "Open Sans", sans-serif;
}

.faq-nav__collapse :deep(.ant-collapse-header-text) {
  flex: 1 1 auto !important;
}

.faq-nav__collapse :deep(.ant-collapse-expand-icon) {
  order: 2 !important;
  margin-inline-start: auto !important;
  margin-inline-end: 0 !important;
  color: var(--main-font) !important;
  font-size: 12px;
}

.faq-nav__collapse :deep(.ant-collapse-header .ant-collapse-expand-icon) {
  padding: 0 !important;
}

.faq-nav__collapse :deep(.ant-collapse-item-active > .ant-collapse-header) {
  background: transparent !important;
}

.faq-nav__collapse :deep(.ant-collapse-header:hover) {
  background: #f7f7f7 !important;
  border-radius: 8px;
}

.faq-nav__collapse :deep(.ant-collapse-content),
.faq-nav__collapse :deep(.ant-collapse-content-box) {
  background: transparent !important;
  border: 0 !important;
  padding: 0 !important;
}

.faq-nav__child {
  display: block;
  padding: 8px 12px 8px 22px;
  color: var(--neutral-black-font);
  text-decoration: none;
  cursor: pointer;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  border-radius: 8px;
}

.faq-nav__child--root {
  padding-left: 12px;
}

.faq-nav__child.is-active {
  background: #f2f2f2;
  font-weight: 600;
}

.faq-nav__item:hover {
  background: #f7f7f7;
}

.faq-nav__child:hover {
  color: #595959;
}
</style>
